import { Box, BoxProps, Flex } from "@chakra-ui/core"
import React from "react"
import Body from "../typography/Body"
import Heading from "../typography/Heading"
import { getFixedPrice, getPriceValue } from "./utils"
import { stringToBase64 } from "../../utils/text"

import { useSelector, useDispatch } from "react-redux"
import { BaseRootState, BaseDispatch } from "redux/store"

type SummaryProps = BoxProps & {
  subtotal?: string
  tax?: string
  total?: string
  totalFees?: string
  pricingData?: {
    id: string
    priceV2?: { amount?: string }
  }[]
}

export default function Summary({
  subtotal,
  tax,
  total,
  totalFees,
  pricingData,
  ...props
}: SummaryProps) {

  const { checkout, quiz } = useSelector(
    (state: BaseRootState) => state
  )

  // Calculate total fees if not provided as prop
  const calculatedTotalFees = React.useMemo(() => {
    if (totalFees !== undefined) {
      return Number(totalFees) || 0
    }
    
    // Calculate from checkout line items if pricingData is available
    if (pricingData && checkout?.data) {
      const lineItems = checkout.data?.lineItems?.edges || checkout.data?.lines?.edges || []
      return lineItems.reduce((sum, { node }) => {
        const attributes = node?.attributes || node?.customAttributes || []
        const typeAttr = attributes.find((attr: any) => attr.key === "_type")
        if (typeAttr?.value === "fee" && node?.merchandise?.id) {
          const feePrice = getPriceValue(
            pricingData,
            stringToBase64(node.merchandise.id)
          )
          return sum + feePrice
        }
        return sum
      }, 0)
    }
    
    return 0
  }, [totalFees, pricingData, checkout])

  const displaySubtotal = React.useMemo(() => {
    if (!subtotal) return subtotal
    const subtotalNum = Number(subtotal) || 0
    const feesNum = calculatedTotalFees || 0
    return (subtotalNum - feesNum).toString()
  }, [subtotal, calculatedTotalFees])

  return (
    <Box {...props}>
      <Heading as="span" d="block" size="6" fontWeight="bold" mb="2.2988rem">
        Order Summary
      </Heading>
      <Flex justify="space-between" color="dawn" mb="0.5625rem">
        <Body size="md">Subtotal</Body>
        <Body size="md">{getFixedPrice(displaySubtotal)}</Body>
      </Flex>
      {tax && tax !== "0.0" && (
        <Flex justify="space-between" color="dawn" mb="0.5625rem">
          <Body size="md">Estimated Tax</Body>
          <Body size="md">{getFixedPrice(tax)}</Body>
        </Flex>
      )}
      {calculatedTotalFees > 0 && (
        <Flex justify="space-between" color="dawn" mb="0.5625rem">
          <Body size="md">Fulfillment and Inspection Fee</Body>
          <Body size="md">{getFixedPrice(calculatedTotalFees.toString())}</Body>
        </Flex>
      )}
      <Flex justify="space-between" color="dawn" mb="0.5625rem">
        <Body size="md">Shipping</Body>
        <Body size="md">Free</Body>
      </Flex>
      {checkout?.data?.discountCodes?.length != null && checkout?.data?.discountCodes?.length > 0 && (
        <Flex justify="space-between" color="dawn" mb="0.5625rem">
          <Body size="md">Extra {quiz?.discount}% Off</Body>
          <Body size="md"
          // @ts-ignore
          >-{getFixedPrice((parseInt(subtotal)/100) * parseInt(quiz?.discount))}</Body>
        </Flex>
      )}
      <Flex justify="space-between" mb="2.0625rem">
        <Body size="md" fontWeight="semibold">
          Total
        </Body>
        <Body size="md" fontWeight="semibold">
          {getFixedPrice(total)}
        </Body>
      </Flex>
    </Box>
  )
}
